import { useState } from 'react';
import { UserCircle, ChevronDown, Camera } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../lib/database.types';
import { useAuth } from '../../context/AuthContext';
import { AIRLINE_OPTIONS } from '../../utils/airlines';
import { COUNTRY_CODES } from '../../utils/countryCodes';
import { getCityName, isValidAirport } from '../../utils/airports';
import { takePhoto, uploadAvatar } from '../../services/cameraService';
import { hashPhone, normalizeToE164 } from '../../utils/phoneHash';
import { saveLocalPhone } from '../../utils/localPhone';
import { resolveReferralHandle } from '../../services/supabaseService';
import { ImageCropper } from '../shared/ImageCropper';

type ProfileInsert = Database['public']['Tables']['profiles']['Insert'];

export function ProfileSetupScreen() {
  const { session, refreshProfile } = useAuth();

  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [selectedAirline, setSelectedAirline] = useState('');
  const [customAirlineCode, setCustomAirlineCode] = useState('');
  const [baseAirport, setBaseAirport] = useState('');
  const [countryCode, setCountryCode] = useState('971'); // Default UAE
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatarDataUri, setAvatarDataUri] = useState<string | null>(null);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('ref') || '';
    } catch { return ''; }
  });
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isOtherAirline = selectedAirline === 'OTHER';
  const effectiveAirlineCode = isOtherAirline ? customAirlineCode.toUpperCase() : selectedAirline;

  const selectedCountry = COUNTRY_CODES.find((c) => c.dial === countryCode);

  const handlePickAvatar = async () => {
    try {
      const photo = await takePhoto();
      if (photo) setCropImage(photo);
    } catch (err) {
      console.warn('[ProfileSetup] takePhoto failed:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }

    const cleanHandle = handle.trim().replace(/^@/, '');
    if (!cleanHandle) {
      setError('Please choose a handle.');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(cleanHandle)) {
      setError('Handle can only contain letters, numbers, and underscores.');
      return;
    }

    if (!effectiveAirlineCode) {
      setError('Please select your airline.');
      return;
    }

    if (isOtherAirline && customAirlineCode.length < 2) {
      setError('Please enter a valid airline IATA code (2 letters).');
      return;
    }

    const cleanBase = baseAirport.trim().toUpperCase();
    if (!cleanBase || cleanBase.length !== 3) {
      setError('Please enter a valid 3-letter IATA airport code.');
      return;
    }
    if (!isValidAirport(cleanBase)) {
      setError(`"${cleanBase}" is not a recognized airport code. Please enter a valid IATA code (e.g. DXB, LHR, JFK).`);
      return;
    }

    const digits = phoneNumber.replace(/\D/g, '');
    const hasPhone = digits.length > 0;
    if (hasPhone && (digits.length < 6 || digits.length > 15)) {
      setError('Please enter a valid phone number (6-15 digits).');
      return;
    }

    if (!session?.user?.id) {
      setError('Session expired. Please sign in again.');
      return;
    }

    setLoading(true);

    // Upload avatar if selected
    let avatarUrl: string | null = null;
    if (avatarDataUri) {
      try {
        avatarUrl = await uploadAvatar(session.user.id, avatarDataUri);
      } catch (err) {
        console.warn('[ProfileSetup] Avatar upload failed:', err);
      }
    }

    let phoneE164: string | null = null;
    let phoneHashValue: string | null = null;
    if (hasPhone) {
      const phoneRaw = `+${countryCode}${digits}`;
      phoneE164 = normalizeToE164(phoneRaw) ?? phoneRaw;
      try {
        phoneHashValue = await hashPhone(phoneE164);
      } catch {
        setError('Failed to process phone number. Please try again.');
        setLoading(false);
        return;
      }
    }

    // Resolve referral code to user ID (warn if invalid, don't block signup)
    let referredBy: string | null = null;
    let referralWarning = '';
    const cleanRef = referralCode.trim().replace(/^@/, '').toLowerCase();
    if (cleanRef) {
      referredBy = await resolveReferralHandle(cleanRef);
      if (referredBy === session.user.id) {
        referredBy = null;
        referralWarning = "You can't refer yourself.";
      } else if (!referredBy) {
        referralWarning = `Referral code "@${cleanRef}" not found — continuing without it.`;
      }
    }

    if (referralWarning) setWarning(referralWarning);

    // Store phone locally — only the hash goes to the server
    if (phoneE164) saveLocalPhone(session.user.id, phoneE164);

    const row: ProfileInsert = {
      id: session.user.id,
      handle: cleanHandle.toLowerCase(),
      name: name.trim(),
      airline_code: effectiveAirlineCode,
      base_airport: cleanBase,
      ...(phoneHashValue ? { phone_hash: phoneHashValue } : {}),
      avatar_url: avatarUrl,
      ...(referredBy ? { referred_by: referredBy } : {}),
    };
    const { error: insertError } = await supabase.from('profiles').insert(row);

    if (insertError) {
      setLoading(false);
      if (insertError.code === '23505') {
        if (insertError.message.includes('handle')) {
          setError('This handle is already taken. Please choose another.');
        } else {
          setError('A profile with this account already exists.');
        }
      } else {
        setError(insertError.message);
      }
      return;
    }

    await refreshProfile();
    setLoading(false);
  };

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6 py-12">
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Complete Your Profile</h1>
        <p className="text-slate-400 text-sm mt-1">Tell us a bit about yourself</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}
        {warning && !error && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 text-amber-400 text-sm">
            {warning}
          </div>
        )}

        {/* Avatar */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handlePickAvatar}
            className="relative w-20 h-20 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center overflow-hidden hover:border-accent transition-colors"
          >
            {avatarDataUri ? (
              <img src={avatarDataUri} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserCircle size={40} className="text-slate-500" />
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 active:opacity-100 transition-opacity rounded-full">
              <Camera size={18} className="text-white" />
            </div>
          </button>
        </div>
        <p className="text-center text-xs text-slate-500 -mt-2">Tap to add a photo</p>

        {/* Name */}
        <div>
          <label htmlFor="profile-name" className="block text-sm font-medium text-slate-400 mb-1.5">
            Name
          </label>
          <input
            id="profile-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
        </div>

        {/* Handle */}
        <div>
          <label htmlFor="profile-handle" className="block text-sm font-medium text-slate-400 mb-1.5">
            Handle
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
              @
            </span>
            <input
              id="profile-handle"
              type="text"
              autoComplete="username"
              value={handle}
              onChange={(e) => setHandle(e.target.value.replace(/^@/, ''))}
              placeholder="yourhandle"
              className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">Letters, numbers, and underscores only</p>
        </div>

        {/* Airline */}
        <div>
          <label htmlFor="profile-airline" className="block text-sm font-medium text-slate-400 mb-1.5">
            Airline
          </label>
          <div className="relative">
            <select
              id="profile-airline"
              value={selectedAirline}
              onChange={(e) => setSelectedAirline(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors appearance-none"
            >
              <option value="" disabled className="text-slate-500">
                Select your airline
              </option>
              {AIRLINE_OPTIONS.map((a) => (
                <option key={a.code} value={a.code} className="bg-slate-700 text-slate-100">
                  {a.name} ({a.code})
                </option>
              ))}
            </select>
            <ChevronDown
              size={18}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Custom airline code (shown when "Other" is selected) */}
        {isOtherAirline && (
          <div>
            <label htmlFor="profile-custom-code" className="block text-sm font-medium text-slate-400 mb-1.5">
              Airline IATA Code
            </label>
            <input
              id="profile-custom-code"
              type="text"
              maxLength={3}
              value={customAirlineCode}
              onChange={(e) => setCustomAirlineCode(e.target.value.toUpperCase())}
              placeholder="e.g. AA"
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors uppercase"
            />
          </div>
        )}

        {/* Home Airport */}
        <div>
          <label htmlFor="profile-base" className="block text-sm font-medium text-slate-400 mb-1.5">
            Home Airport
          </label>
          <input
            id="profile-base"
            type="text"
            maxLength={3}
            value={baseAirport}
            onChange={(e) => setBaseAirport(e.target.value.toUpperCase())}
            placeholder="e.g. DXB"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors uppercase"
          />
          {(() => {
            if (baseAirport.length < 3) {
              return <p className="mt-1 text-xs text-slate-500">The airport you fly from and return to</p>;
            }
            return isValidAirport(baseAirport.toUpperCase()) ? (
              <p className="mt-1 text-xs text-accent font-medium">{getCityName(baseAirport.toUpperCase())}</p>
            ) : (
              <p className="mt-1 text-xs text-red-400">Not a recognized airport code</p>
            );
          })()}
        </div>

        {/* Phone with Country Code (optional) */}
        <div>
          <label htmlFor="profile-phone" className="block text-sm font-medium text-slate-400 mb-1.5">
            Phone Number <span className="text-slate-600 font-normal">(optional)</span>
          </label>
          <p className="text-xs text-slate-400 mb-2">
            Add your number to find crew already on Rost & Found. Your number is hashed on-device — only the hash is stored.
          </p>
          <div className="flex gap-2">
            <div className="relative shrink-0">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-[110px] px-3 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors appearance-none text-sm"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.dial} className="bg-slate-700 text-slate-100">
                    {c.flag} +{c.dial}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
              />
            </div>
            <input
              id="profile-phone"
              type="tel"
              autoComplete="tel-national"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s]/g, ''))}
              placeholder="170 1234567"
              className="flex-1 px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name} — ` : ''}You can always add this later in Settings
          </p>
        </div>

        {/* Referral Code (optional) */}
        <div>
          <label htmlFor="profile-referral" className="block text-sm font-medium text-slate-400 mb-1.5">
            Referral Code <span className="text-slate-600 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
              @
            </span>
            <input
              id="profile-referral"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.replace(/^@/, ''))}
              placeholder="friend's handle"
              className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">Referred by a friend? Enter their handle</p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
        >
          {loading ? 'Setting up...' : 'Complete Setup'}
        </button>
      </form>

      {cropImage && (
        <ImageCropper
          image={cropImage}
          onCancel={() => setCropImage(null)}
          onConfirm={(cropped) => {
            setCropImage(null);
            setAvatarDataUri(cropped);
          }}
        />
      )}
    </div>
  );
}
